import React, { useState } from 'react';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useColors } from '../../../../../theme/ColorModeContext';
import { StageSection, Prose, KeyPoints, NumberedFlow, Callout, MiniLabel, Panel, SegmentedToggle } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';
import { DvpConditions, LegOutcome, settleDvp } from './cantonModel';
import { Verdict } from './cantonVisuals';

export const meta = {
    title: 'Composability & Atomicity Across Parties',
    tagline: 'Multi-party workflows that succeed or fail together.',
};

type SettlementMode = 'atomic' | 'separate';

const CONDITION_LABELS: [keyof DvpConditions, string][] = [
    ['cashActive', 'Alice\'s $100 cash Iou is still active'],
    ['bankConfirms', 'Bank\'s node approves the cash leg'],
    ['sharesActive', 'Bob\'s 10 ACME shares are still active'],
    ['registryConfirms', 'Registry\'s node approves the share leg'],
];

const LegBadge = ({ outcome }: { outcome: LegOutcome }) => {
    const colors = useColors();
    const style = {
        committed: { label: 'COMMITTED', color: colors.accent.green },
        failed: { label: 'FAILED', color: colors.accent.orange },
        'rolled-back': { label: 'ROLLED BACK', color: colors.text.muted },
    }[outcome];
    return (
        <Typography component="span" sx={{ color: style.color, fontWeight: 800, fontSize: '0.75rem', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
            {style.label}
        </Typography>
    );
};

const DvpSimulator = () => {
    const colors = useColors();
    const [mode, setMode] = useState<SettlementMode>('atomic');
    const [conditions, setConditions] = useState<DvpConditions>({ cashActive: true, sharesActive: true, bankConfirms: true, registryConfirms: true });
    const outcome = settleDvp(conditions, mode === 'atomic');
    const settled = outcome.cashLeg === 'committed' && outcome.shareLeg === 'committed';

    const verdictTitle = settled
        ? 'Settled: both sides delivered'
        : outcome.stranded
            ? 'Settlement failure: one side gave without receiving'
            : 'Nothing happened: both sides keep what they had';

    const holdingRow = (name: string, before: string, after: string) => (
        <Box key={name} sx={{ display: 'grid', gridTemplateColumns: '60px 1fr auto 1fr', gap: '8px', alignItems: 'center', py: 0.75, borderBottom: `1px solid ${colors.border.subtle}` }}>
            <Typography sx={{ color: colors.text.primary, fontWeight: 800, fontSize: '0.88rem' }}>{name}</Typography>
            <Typography sx={{ color: colors.text.muted, fontSize: '0.85rem', fontFamily: '"Share Tech Mono", monospace' }}>{before}</Typography>
            <Typography sx={{ color: colors.text.muted }}>→</Typography>
            <Typography sx={{ color: before === after ? colors.text.secondary : colors.text.primary, fontSize: '0.85rem', fontFamily: '"Share Tech Mono", monospace', fontWeight: before === after ? 400 : 800 }}>{after}</Typography>
        </Box>
    );
    const fmt = (cash: number, shares: number) => `$${cash} · ${shares} ACME`;

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: '18px' }}>
            <Panel>
                <MiniLabel>How is the trade settled?</MiniLabel>
                <SegmentedToggle<SettlementMode>
                    ariaLabel="Settlement mode"
                    value={mode}
                    onChange={setMode}
                    options={[{ value: 'atomic', label: 'One Canton transaction' }, { value: 'separate', label: 'Two separate transfers' }]}
                />
                <Box sx={{ mt: 2.5 }}>
                    <MiniLabel>Break something</MiniLabel>
                    {CONDITION_LABELS.map(([key, label]) => (
                        <FormControlLabel
                            key={key}
                            sx={{ display: 'flex', mx: 0, '& .MuiFormControlLabel-label': { color: colors.text.secondary, fontSize: '0.9rem' } }}
                            control={
                                <Switch
                                    checked={conditions[key]}
                                    onChange={(event) => setConditions((current) => ({ ...current, [key]: event.target.checked }))}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: colors.accent.green },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.accent.green },
                                    }}
                                />
                            }
                            label={label}
                        />
                    ))}
                </Box>
            </Panel>
            <Panel borderColor={settled ? colors.accent.green : outcome.stranded ? colors.accent.red : colors.accent.orange}>
                <MiniLabel>Outcome</MiniLabel>
                <Box sx={{ display: 'grid', gap: '6px', mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                        <Typography sx={{ color: colors.text.secondary, fontSize: '0.9rem' }}>Cash leg: $100 Alice → Bob</Typography>
                        <LegBadge outcome={outcome.cashLeg} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                        <Typography sx={{ color: colors.text.secondary, fontSize: '0.9rem' }}>Share leg: 10 ACME Bob → Alice</Typography>
                        <LegBadge outcome={outcome.shareLeg} />
                    </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                    {holdingRow('Alice', fmt(100, 0), fmt(outcome.alice.cash, outcome.alice.shares))}
                    {holdingRow('Bob', fmt(0, 10), fmt(outcome.bob.cash, outcome.bob.shares))}
                </Box>
                <Verdict ok={settled} title={verdictTitle}>
                    {settled && 'Every involved node approved its own part, so the mediator committed the whole transaction at once.'}
                    {!settled && !outcome.stranded && (
                        <>
                            {outcome.failures.join('; ')}.{' '}
                            {mode === 'atomic'
                                ? 'Because both legs are one transaction, the failure of any part rejects all of it, even the parts that were fine on their own.'
                                : 'Both transfers failed independently, so by luck neither side is out of pocket this time.'}
                        </>
                    )}
                    {outcome.stranded && (
                        <>
                            {outcome.failures.join('; ')}, but the other transfer went through anyway.{' '}
                            {outcome.cashLeg === 'committed' ? 'Alice paid $100 and received no shares.' : 'Bob gave up the shares and received no cash.'}{' '}
                            This is settlement risk. Switch to one Canton transaction and it disappears.
                        </>
                    )}
                </Verdict>
            </Panel>
        </Box>
    );
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - atomicity across multiple parties/contracts in one transaction
//   Q1 - what cross-synchronizer composability enables
export const blocks: StageBlock[] = [
    content('atomicity', () => (
        <StageSection gradientBorder eyebrow="Stage 7 of 8" title="One workflow, several parties, one outcome">
            <Prose>
                Real financial workflows rarely touch just one contract. A securities trade moves cash one way and
                securities the other; a repo also involves collateral; a corporate action can touch thousands of
                holders. The worst outcome is a <em>half-finished</em> workflow: one side delivers, the other
                doesn't. This is called <strong>settlement risk</strong>, and it's not theoretical. In 1974 German
                regulators closed Bankhaus Herstatt at the end of the German business day, after counterparties had
                paid it Deutsche Marks but before it paid out the dollars it owed them in New York. The failure was big enough that
                this kind of risk is still called "Herstatt risk".
            </Prose>
            <Prose>
                Canton can compose many contract updates, spanning many parties and many applications, into a single{' '}
                <strong>atomic</strong> transaction. Either the entire workflow commits, or none of it does. That's
                the same guarantee you've seen elsewhere (Ethereum's all-or-nothing transactions, Solana's atomically
                bundled instructions), applied across independently operated nodes that can't even see each other's
                parts of the transaction.
            </Prose>
            <Prose>
                How does that work when Bank can't see the share leg and Registry can't see the cash leg? Recall the
                protocol from Stage 5: every involved node validates only its own view and sends its response to
                the mediator, which only announces "commit" when every required approval is in. One rejection
                anywhere, from any node, and the mediator rejects the whole transaction for everyone.
            </Prose>
            <DvpSimulator />
        </StageSection>
    )),

    question(0),

    content('cross-synchronizer', () => (
        <StageSection title="Interoperating across separate deployments">
            <Prose>
                Atomicity inside one synchronizer is only half the story. Different institutions may use different
                synchronizers, so what happens if Alice's cash lives on the Global Synchronizer while Bob's shares
                sit on a market operator's private one? A single transaction is always ordered by one synchronizer,
                but contracts aren't stuck where they were created. Canton can <strong>reassign</strong> a contract
                from one synchronizer to another, as long as its stakeholders' nodes are connected to both:
            </Prose>
            <NumberedFlow steps={[
                ['Unassign', 'The share contract is released from the market synchronizer. Its stakeholders\' nodes record that it\'s in transit.'],
                ['Assign', 'The same contract, with the same ID and contents, is assigned to the Global Synchronizer, where the cash already is.'],
                ['Settle atomically', 'Now that both inputs share a synchronizer, the DvP runs as one transaction there, all-or-nothing exactly as in the simulator.'],
            ]} />
            <Prose>
                This is what makes Canton a <em>network of networks</em> instead of a set of isolated private
                ledgers. It enables the kind of composability DeFi users take for granted (an asset issued by one
                application used as an input to another application's workflow) but between regulated institutions,
                with privacy intact, and without everyone having to join a single shared network on day one.
            </Prose>
            <Callout tone="security" title="Security angle: composition widens the blast radius">
                Composability cuts both ways. A workflow that pulls in contracts from several applications trusts
                all of their templates, and a choice that exercises another contract carries authority into it.
                Reassignments add their own questions: which synchronizer is a contract on right now, and who must
                be connected to move it? Cross-contract authority and state consistency are recurring themes in the
                advanced DAML tracks.
            </Callout>
            <KeyPoints items={[
                <>Atomicity holds across parties, contracts and applications, not just within a single contract.</>,
                <>Nodes that can't see each other's parts of a transaction still commit or reject it together.</>,
                <>Reassignment lets contracts move between synchronizers, so separately deployed Canton networks can interoperate rather than staying siloed.</>,
            ]} />
        </StageSection>
    )),

    question(1),
];
